import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const ParentAcademicPerformance = ({ onNavigate, userInfo }) => {
  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentData, setParentData] = useState({
    name: "Loading...",
    parentId: "...",
    relation: "Parent",
    profileImage: null
  });

  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    rollNumber: "...",
    course: "...",
    department: "...",
    year: "...",
    batch: "..."
  });

  const [academicData, setAcademicData] = useState({
    semesterWisePerformance: [],
    overallStats: {
      totalSubjects: 0,
      averageMarks: 0
    }
  });

  // Fetch Academic Performance Data from API
  useEffect(() => {
    fetchAcademicData();
  }, [userInfo]);

  const fetchAcademicData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Starting API call to fetch academic performance data...');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');

      if (!token) {
        console.log('❌ No token found, using fallback data');
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      // Fetch parent profile data first for sidebar
      const profileUrl = `${API_LINK}/api/parent/profile`;
      console.log('🌐 Profile API URL:', profileUrl);

      const profileResponse = await fetch(profileUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Profile Response status:', profileResponse.status);

      if (profileResponse.ok) {
        const profileResult = await profileResponse.json();
        console.log('📦 Profile API Response:', profileResult);

        if (profileResult.success && profileResult.data) {
          const data = profileResult.data;
          console.log('✅ Processing profile API data...');

          // Update parent data
          setParentData({
            name: data.name || "Parent",
            parentId: data.parentId || "PARENT-001",
            relation: data.relation || "Parent",
            profileImage: data.profileImage || null
          });
        }
      }

      // Fetch academic performance data
      const academicUrl = `${API_LINK}/api/parent/student/academic-performance`;
      console.log('🌐 Academic API URL:', academicUrl);

      const academicResponse = await fetch(academicUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Academic Response status:', academicResponse.status);

      if (!academicResponse.ok) {
        if (academicResponse.status === 401) {
          console.log('🔐 Authentication failed');
          setError('Session expired. Please login again.');
          handleLogout();
          return;
        } else if (academicResponse.status === 404) {
          console.log('🔍 API endpoint not found');
          setError('Academic data endpoint not found. Please check server configuration.');
          setLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${academicResponse.status}`);
      }

      const academicResult = await academicResponse.json();
      console.log('📦 Academic API Response:', academicResult);

      if (academicResult.success && academicResult.data) {
        const data = academicResult.data;
        console.log('✅ Processing academic API data...');

        // Update student data
        setStudentData({
          name: data.student?.name || "Student",
          studentId: data.student?.studentId || "STU-001",
          rollNumber: data.student?.rollNumber || "...",
          course: data.student?.course || "BCA",
          department: data.student?.department || "Computer Science",
          year: data.student?.year || "3rd Year",
          batch: data.student?.batch || "2022-2025"
        });

        // Update academic data
        setAcademicData({
          semesterWisePerformance: data.semesterWisePerformance || [],
          overallStats: data.overallStats || { totalSubjects: 0, averageMarks: 0 }
        });

        console.log('✅ Academic performance data updated successfully');

      } else {
        console.log('❌ Academic API returned error:', academicResult.message);
        setError(academicResult.message || 'Failed to fetch academic performance data');
      }

    } catch (error) {
      console.error('💥 Error fetching academic performance data:', error);

      if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
        setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
      } else if (error.message.includes('fetch')) {
        setError('Cannot connect to server. Please ensure the server is running');
      } else {
        setError(`Failed to load academic performance data: ${error.message}`);
      }

      if (userInfo) {
        console.log('🔄 Using fallback userInfo data');
        setParentData(prev => ({
          ...prev,
          name: userInfo.name || prev.name,
          parentId: userInfo.id || prev.parentId
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');
    sessionStorage.clear();
    localStorage.clear();

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    switch (page) {
      case 'Dashboard':
        onNavigate('parent-dashboard');
        break;
      case 'Academic Performance':
        // Stay on current page since this is the academic performance component
        break;
      case 'Fee Status':
        onNavigate('parent-fees-status');
        break;
      case 'Holidays & Events':
        onNavigate('parent-holidays');
        break;
      case 'Emergency Alert':
        onNavigate('parent-emergency-alert');
        break;
      case 'Logout':
        handleLogout();
        break;
      default:
        console.log(`Navigation to ${page} clicked`);
    }
  };

  // Calculate CGPA based on marks
  const calculateCGPA = (marks) => {
    if (!marks || marks.length === 0) return "0.00";
    const totalMarks = marks.reduce((sum, mark) => sum + (parseFloat(mark.totalMarks) || 0), 0);
    const averageMarks = totalMarks / marks.length;
    return (averageMarks / 10).toFixed(2);
  };

  // Get grade color
  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return { backgroundColor: '#c8e6c9', color: '#2e7d32' };
      case 'B+':
      case 'B':
        return { backgroundColor: '#bbdefb', color: '#1565c0' };
      case 'C+':
      case 'C':
        return { backgroundColor: '#fff9c4', color: '#f9a825' };
      default:
        return { backgroundColor: '#ffcdd2', color: '#c62828' };
    }
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Academic Performance...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Academic Performance
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="parent"
      onNavigate={handleNavigation}
      parentData={parentData}
      activePage="Academic Performance"
      pageTitle="Academic Performance"
    >
      {/* Student Info Header */}
      <section style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          marginBottom: '1.5rem',
          fontSize: '2rem'
        }}>
          <span style={{ color: '#238548' }}>🎓</span>
          Academic Performance - {studentData.name}
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem'
        }}>
          <div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Student ID:</strong> {studentData.studentId}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Roll Number:</strong> {studentData.rollNumber}</p>
          </div>
          <div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Course:</strong> {studentData.course}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Department:</strong> {studentData.department}</p>
          </div>
          <div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Year:</strong> {studentData.year}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Batch:</strong> {studentData.batch}</p>
          </div>
        </div>
      </section>

      {/* Overall Statistics */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {[
          {
            icon: '📈',
            title: 'Overall CGPA',
            value: academicData.overallStats.averageMarks ? (academicData.overallStats.averageMarks / 10).toFixed(2) : '0.00',
            subtitle: 'Cumulative Performance'
          },
          {
            icon: '📚',
            title: 'Total Subjects',
            value: academicData.overallStats.totalSubjects || 0,
            subtitle: 'Across All Semesters'
          },
          {
            icon: '🎯',
            title: 'Average Marks',
            value: academicData.overallStats.averageMarks ? `${academicData.overallStats.averageMarks.toFixed(1)}%` : '0%',
            subtitle: 'Overall Performance'
          },
          {
            icon: '📅',
            title: 'Semesters Completed',
            value: academicData.semesterWisePerformance.length || 0,
            subtitle: 'Academic Progress'
          }
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            transition: 'all 0.3s ease'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
            }}>
            <div style={{
              fontSize: '2.5rem',
              color: '#238548',
              marginBottom: '1rem'
            }}>
              {stat.icon}
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>{stat.title}</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: '#238548',
              margin: '0.5rem 0'
            }}>
              {stat.value}
            </p>
            <p style={{ color: '#666', margin: 0, fontSize: '0.9rem' }}>
              {stat.subtitle}
            </p>
          </div>
        ))}
      </section>

      {/* Semester-wise Performance */}
      <section style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span style={{ fontSize: '1.8rem' }}>📊</span>
          <h3 style={{ margin: 0, fontSize: '1.3rem' }}>Semester-wise Performance</h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {academicData.semesterWisePerformance.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {academicData.semesterWisePerformance.map((semester, index) => (
                <div key={index} style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: '#f8fafc',
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem'
                  }}>
                    <h4 style={{ margin: 0, color: '#333', fontSize: '1.1rem' }}>
                      {semester.semester}
                    </h4>
                    <div style={{ display: 'flex', gap: '1.5rem' }}>
                      <span style={{ color: '#666', fontSize: '0.9rem' }}>
                        <strong>SGPA:</strong> {calculateCGPA(semester.subjects)}
                      </span>
                      <span style={{ color: '#666', fontSize: '0.9rem' }}>
                        <strong>Total Subjects:</strong> {semester.subjects.length}
                      </span>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                    }}>
                      <thead>
                        <tr style={{ background: '#ffffff', borderBottom: '1px solid #ecf0f1' }}>
                          <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Subject Code</th>
                          <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Subject Name</th>
                          <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Marks Obtained</th>
                          <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Total Marks</th>
                          <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {semester.subjects.map((subject, subIndex) => (
                          <tr key={subIndex} style={{
                            borderBottom: subIndex < semester.subjects.length - 1 ? '1px solid #ecf0f1' : 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <td style={{ padding: '1rem 1.5rem', color: '#333' }}>{subject.code || 'N/A'}</td>
                            <td style={{ padding: '1rem 1.5rem', color: '#333', fontWeight: 500 }}>{subject.name}</td>
                            <td style={{ padding: '1rem 1.5rem', color: '#333' }}>{subject.marksObtained}</td>
                            <td style={{ padding: '1rem 1.5rem', color: '#666' }}>{subject.totalMarks}</td>
                            <td style={{ padding: '1rem 1.5rem' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                ...getGradeColor(subject.grade)
                              }}>
                                {subject.grade}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📊</div>
              <h3 style={{ color: '#666', marginBottom: '0.5rem' }}>No Academic Records Found</h3>
              <p style={{ color: '#999', margin: 0 }}>Academic performance data is not available at this time.</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ParentAcademicPerformance;